import { WordPressPost } from '../../types/wordpress';
import { HTMLPosts } from '../../types/blog';
import { devLog } from '../../utils';

export interface TokenMap {
  [key: string]: any;
}

export interface TemplateProcessingOptions {
  preserveWhitespace?: boolean;
  defaultValues?: { [key: string]: string };
  dateFormat?: 'long' | 'short' | 'iso';
}

export class TemplateEngine {
  private readonly TOKEN_REGEX = /\{\{([^}]+)\}\}/g;
  private readonly TAG_TOKEN = '{{tags}}';
  private readonly DEFAULT_TAG_FALLBACK = 'Uncategorized';

  /**
   * Process a template with a single data object
   */
  processTemplate(template: string, data: any, options: TemplateProcessingOptions = {}): string {
    if (!template || !data) {
      return template || '';
    }

    let processedTemplate = template;

    // Handle special tags token first
    if (this.isWordPressPost(data)) {
      processedTemplate = this.processTagsToken(processedTemplate, data);
    }

    // Get flattened token map
    const tokenMap = this.flattenObject(data);

    // Process date tokens
    this.processDateTokens(tokenMap, options.dateFormat);

    // Replace all tokens
    processedTemplate = this.replaceTokens(processedTemplate, tokenMap, options.defaultValues);

    // Handle missing tag fallbacks
    processedTemplate = this.handleTagFallbacks(processedTemplate);

    return processedTemplate;
  }

  /**
   * Process a template with multiple data objects (for lists)
   */
  processMultipleItems(
    template: string,
    items: any[],
    options: TemplateProcessingOptions = {}
  ): HTMLPosts {
    if (!template || !items || items.length === 0) {
      return { __html: '' };
    }

    const processedItems = items.map((item) => this.processTemplate(template, item, options));

    return { __html: processedItems.join(' ') };
  }

  /**
   * Extract all tokens from a template
   */
  extractTokens(template: string): string[] {
    const tokens: string[] = [];
    let match;

    while ((match = this.TOKEN_REGEX.exec(template)) !== null) {
      tokens.push(match[1].trim());
    }

    return Array.from(new Set(tokens)); // Remove duplicates
  }

  /**
   * Validate that all required tokens have data
   */
  validateTemplate(template: string, data: any): { valid: boolean; missingTokens: string[] } {
    const tokens = this.extractTokens(template);
    const tokenMap = this.flattenObject(data);
    const missingTokens = tokens.filter((token) => !(token in tokenMap));

    return {
      valid: missingTokens.length === 0,
      missingTokens,
    };
  }

  /**
   * Create a preview of what tokens will be replaced with
   */
  previewTokenReplacements(template: string, data: any): { [token: string]: any } {
    const tokens = this.extractTokens(template);
    const tokenMap = this.flattenObject(data);
    const preview: { [token: string]: any } = {};

    tokens.forEach((token) => {
      preview[token] = tokenMap[token] ?? `[Missing: ${token}]`;
    });

    return preview;
  }

  // Private helper methods

  private isWordPressPost(data: any): data is WordPressPost {
    return data && typeof data === 'object' && 'title' in data && '_embedded' in data;
  }

  private processTagsToken(template: string, post: WordPressPost): string {
    if (!template.includes(this.TAG_TOKEN)) {
      return template;
    }

    const terms = post._embedded?.['wp:term'];
    const tags = terms?.[1]; // Index 1 is for tags (0 is categories)

    if (!tags || tags.length === 0) {
      return template.replace(this.TAG_TOKEN, '');
    }

    const tagElements = tags
      .map((tag, index) => `<div class='category-type'>{{_embedded.wp:term.1.${index}.name}}</div>`)
      .join('');

    return template.replace(this.TAG_TOKEN, tagElements);
  }

  private flattenObject(obj: any, prefix = ''): TokenMap {
    const flattened: TokenMap = {};

    if (obj === null || obj === undefined) {
      return flattened;
    }

    if (typeof obj !== 'object') {
      if (prefix) {
        flattened[prefix] = obj;
      }
      return flattened;
    }

    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value === null || value === undefined) {
        flattened[newKey] = '';
      } else if (Array.isArray(value)) {
        // Handle arrays by creating indexed keys
        value.forEach((item, index) => {
          const arrayKey = `${newKey}.${index}`;
          Object.assign(flattened, this.flattenObject(item, arrayKey));
        });
        // Also add the array length
        flattened[`${newKey}.length`] = value.length;
      } else if (typeof value === 'object') {
        Object.assign(flattened, this.flattenObject(value, newKey));
      } else {
        flattened[newKey] = value;
      }
    });

    return flattened;
  }

  private processDateTokens(tokenMap: TokenMap, format: 'long' | 'short' | 'iso' = 'long'): void {
    Object.keys(tokenMap).forEach((key) => {
      if (key.includes('date') && tokenMap[key]) {
        try {
          const date = new Date(tokenMap[key]);
          if (!isNaN(date.getTime())) {
            tokenMap[key] = this.formatDate(date, format);
          }
        } catch (error) {
          // If date parsing fails, keep original value
          devLog.warn(`Failed to parse date for token ${key}:`, error);
        }
      }
    });
  }

  private formatDate(date: Date, format: 'long' | 'short' | 'iso'): string {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const month = monthNames[date.getMonth()];
    switch (format) {
      case 'short':
        return date.toLocaleDateString();
      case 'iso':
        return date.toISOString();
      case 'long':
      default:
        return `${month} ${date.getDate()}, ${date.getFullYear()}`;
    }
  }

  private replaceTokens(
    template: string,
    tokenMap: TokenMap,
    defaultValues: { [key: string]: string } = {}
  ): string {
    return template.replace(this.TOKEN_REGEX, (match, token) => {
      const trimmedToken = token.trim();

      if (trimmedToken in tokenMap) {
        const value = tokenMap[trimmedToken];
        return value !== null && value !== undefined ? String(value) : '';
      }

      if (trimmedToken in defaultValues) {
        return defaultValues[trimmedToken];
      }

      // Return empty string for missing tokens
      return '';
    });
  }

  private handleTagFallbacks(template: string): string {
    // Handle the specific case where tag names are missing and should default to "Uncategorized"
    const tagNamePattern = /\{\{_embedded\.wp:term\.1\.0\.name\}\}/g;
    return template.replace(tagNamePattern, this.DEFAULT_TAG_FALLBACK);
  }
}

// Export a singleton instance for convenience
export const templateEngine = new TemplateEngine();
